/**
 * AccountPriceGuidanceSection — richtprijzen die de klant-assistent bij dít
 * account mag noemen. Het accountteam ziet per projecttype wat de historie
 * zegt (mediaan, bandbreedte, aantal), stelt de goedgekeurde bandbreedte bij,
 * vinkt regels aan en zet bovenaan de hoofdschakelaar om. Zonder die
 * schakelaar noemt de assistent geen bedragen — ook niet het huistarief.
 *
 * Handmatige regels ("Flyer A5, 500 stuks") zijn voor klanten zonder
 * videohistorie, zoals de MKB-pilot. Opslaan gebeurt bij blur/klik; geen
 * opslaan-knop.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, IconButton, MenuItem, Paper, Select, Stack, Switch,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HistoryIcon from '@mui/icons-material/History';
import { authFetch } from '../../utils/authHelpers';
import { useUserRole } from '../../contexts/UserRoleContext';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { bevestig } from '../../utils/bevestig';

const UNITS = ['project', 'stuk', 'uur', 'dag', 'minuut film'];
const eur = (v) => (v == null ? '—' : new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v));
const when = (iso) => (iso ? new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function PriceField({ value, onSave, disabled }) {
  const [v, setV] = useState(value == null ? '' : String(value));
  useEffect(() => { setV(value == null ? '' : String(value)); }, [value]);
  return (
    <TextField
      size="small" value={v} disabled={disabled}
      onChange={(e) => setV(e.target.value.replace(/[^\d.,]/g, ''))}
      onBlur={() => {
        const num = v === '' ? null : Number(String(v).replace(',', '.'));
        if (num !== value && !(num == null && value == null)) onSave(Number.isFinite(num) ? num : null);
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      placeholder="—"
      slotProps={{ input: { startAdornment: <Typography sx={{ mr: 0.5, color: '#94a3b8', fontSize: 13 }}>€</Typography>, sx: { fontSize: 13.5, width: 120 } } }}
    />
  );
}

export default function AccountPriceGuidanceSection({ accountId, accountName }) {
  // Server weigert zonder offertes.edit; de UI verbergt dan de knoppen zodat het geen doodlopende straat is.
  const { permissions } = useUserRole();
  const canEdit = !!permissions?.offertes?.edit;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newUnit, setNewUnit] = useState('stuk');

  const base = `${AI_ENGINE_URL}/api/v1/price-guidance`;

  const load = useCallback(async () => {
    try {
      const r = await authFetch(`${base}/account/${accountId}?account_name=${encodeURIComponent(accountName || '')}`);
      if (!r.ok) throw new Error('Richtprijzen laden mislukt');
      setData(await r.json());
      setError(null);
    } catch (e) { setError(e.message); }
  }, [accountId, accountName, base]);

  useEffect(() => { load(); }, [load]);

  const call = async (url, method, body) => {
    setBusy(true);
    try {
      const r = await authFetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.detail || 'Opslaan mislukt');
      await load();
      return j;
    } catch (e) { setError(e.message); return null; }
    finally { setBusy(false); }
  };

  const setSettings = (patch) => call(`${base}/account/${accountId}/settings`, 'PUT', { ...patch, account_name: accountName });
  const saveRow = (row, patch) => call(`${base}/rows`, 'POST', { id: row.id, ...patch });
  const addRow = async (label, unit, kind = 'item') => {
    if (!label.trim()) return;
    await call(`${base}/rows`, 'POST', { account_airtable_id: accountId, kind, label: label.trim(), unit });
    setNewLabel('');
  };
  const fill = () => call(`${base}/account/${accountId}/fill`, 'POST', {});
  const remove = async (row) => {
    if (!(await bevestig({ titel: 'Richtprijs-regel verwijderen?', tekst: `"${row.label || 'Deze regel'}" verdwijnt uit wat de assistent mag noemen. Dit kan niet ongedaan worden gemaakt.`, knop: 'Verwijderen', gevaarlijk: true }))) return;
    await call(`${base}/rows/${row.id}`, 'DELETE');
  };

  const enabledCount = useMemo(() => (data?.rows || []).filter((r) => r.enabled).length, [data]);

  if (!data && !error) return <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={22} /></Box>;

  const settings = data?.settings || {};
  const rows = data?.rows || [];
  const unsaved = data?.unsaved_suggestions || [];

  return (
    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="subtitle1" fontWeight={800}>Richtprijzen voor de klant-assistent</Typography>
          <Typography variant="body2" color="text.secondary">
            Wat de assistent bij {accountName || 'dit account'} mag noemen als iemand vraagt "wat kost…". Altijd als bandbreedte, excl. btw, altijd gevolgd door contact met jullie.
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2" fontWeight={700} sx={{ color: settings.pricing_enabled ? '#059669' : '#64748b' }}>
            {settings.pricing_enabled ? 'Assistent mag prijzen noemen' : 'Assistent noemt geen prijzen'}
          </Typography>
          <Switch checked={!!settings.pricing_enabled} disabled={busy || !canEdit} color="success"
            onChange={(e) => setSettings({ pricing_enabled: e.target.checked })} />
        </Stack>
      </Stack>

      {settings.pricing_enabled && enabledCount === 0 && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>De schakelaar staat aan, maar er is nog geen regel aangevinkt — de assistent kan zo nog niets noemen.</Alert>
      )}
      {settings.verified_by && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          Laatst geverifieerd door {settings.verified_by}{settings.verified_at ? ` op ${when(settings.verified_at)}` : ''}.
        </Typography>
      )}
      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1.5 }}>{error}</Alert>}

      <Table size="small" sx={{ '& td, & th': { py: 0.75, fontSize: 13.5 } }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 700 }}>Wat</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Eenheid</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>
              <Tooltip title="Mediaan en p25–p75 van uitgevoerde projecten van dit account (laatste 24 maanden; geannuleerd, afgewezen en nog-in-offerte tellen niet mee), afgerond."><span>Historie (uitgevoerd)</span></Tooltip>
            </TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Richtprijs van</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>tot</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Aan</TableCell>
            <TableCell sx={{ fontWeight: 700 }}>Notitie</TableCell>
            <TableCell />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} sx={{ opacity: r.enabled ? 1 : 0.75 }}>
              <TableCell>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: 13.5, fontWeight: 600 }}>{r.label}</Typography>
                  {r.kind === 'item' && <Chip size="small" label="handmatig" sx={{ height: 18, fontSize: 11 }} />}
                </Stack>
              </TableCell>
              <TableCell>
                <Select size="small" value={r.unit || 'project'} disabled={!canEdit} onChange={(e) => saveRow(r, { unit: e.target.value })} sx={{ fontSize: 13, minWidth: 110 }} variant="standard" disableUnderline>
                  {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                </Select>
              </TableCell>
              <TableCell>
                {r.suggested ? (
                  <Tooltip title={`${r.suggested.n} projecten, tot ${when(r.suggested.until)}`}>
                    <Typography sx={{ fontSize: 13, color: '#475569', fontVariantNumeric: 'tabular-nums' }}>
                      {eur(r.suggested.median)} <span style={{ color: '#94a3b8' }}>({eur(r.suggested.min)}–{eur(r.suggested.max)}, n={r.suggested.n})</span>
                      {r.suggested.confidence === 'laag' && <Chip size="small" label="zwak signaal" sx={{ ml: 0.75, height: 16, fontSize: 10, color: '#b45309', bgcolor: '#fef3c7' }} />}
                    </Typography>
                  </Tooltip>
                ) : <Typography sx={{ fontSize: 13, color: '#94a3b8' }}>geen historie</Typography>}
              </TableCell>
              <TableCell><PriceField value={r.price_min} disabled={busy || !canEdit} onSave={(v) => saveRow(r, { price_min: v })} /></TableCell>
              <TableCell><PriceField value={r.price_max} disabled={busy || !canEdit} onSave={(v) => saveRow(r, { price_max: v })} /></TableCell>
              <TableCell>
                <Switch size="small" checked={!!r.enabled} disabled={busy || !canEdit} onChange={(e) => saveRow(r, { enabled: e.target.checked })} />
              </TableCell>
              <TableCell sx={{ minWidth: 180 }}>
                <TextField size="small" variant="standard" fullWidth placeholder="bv. incl. 1 draaidag" defaultValue={r.notes || ''} disabled={!canEdit}
                  onBlur={(e) => { if ((e.target.value || null) !== (r.notes || null)) saveRow(r, { notes: e.target.value }); }}
                  slotProps={{ input: { disableUnderline: true, sx: { fontSize: 13 } } }} />
              </TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => remove(r)} disabled={busy || !canEdit} title="Regel verwijderen"><DeleteOutlineIcon fontSize="small" /></IconButton>
              </TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow><TableCell colSpan={8}><Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>Nog geen regels. Vul aan uit de historie, of voeg zelf een regel toe.</Typography></TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      {canEdit && (
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 2, flexWrap: 'wrap' }}>
        <Button size="small" variant="outlined" startIcon={<HistoryIcon />} disabled={busy} onClick={fill}>
          {rows.some((r) => r.kind === 'projecttype') ? 'Historie herberekenen' : 'Vul aan uit historie'}
          {unsaved.length > 0 ? ` (${unsaved.length} nieuw)` : ''}
        </Button>
        <TextField size="small" placeholder="Nieuwe regel, bv. Flyer A5 (500 stuks)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') addRow(newLabel, newUnit); }} sx={{ minWidth: 260 }} slotProps={{ input: { sx: { fontSize: 13.5 } } }} />
        <Select size="small" value={newUnit} onChange={(e) => setNewUnit(e.target.value)} sx={{ fontSize: 13 }}>
          {UNITS.map((u) => <MenuItem key={u} value={u}>{u}</MenuItem>)}
        </Select>
        <Button size="small" variant="contained" startIcon={<AddIcon />} disabled={busy || !newLabel.trim()} onClick={() => addRow(newLabel, newUnit)}>Toevoegen</Button>
      </Stack>
      )}

      {unsaved.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          In de historie maar nog niet als regel: {unsaved.map((s) => `${s.label} (${eur(s.median)}, n=${s.n})`).join(' · ')}.
        </Typography>
      )}
    </Paper>
  );
}
